// The guided tour of the interface (components/Tour.tsx): its steps, and the rules that the tests check. A step
// points at an element marked `data-tour="…"`; a step whose element is not on screen (feature not in the plan,
// role without it, hidden at this width) is left out; a step without a target shows centred.

import type { en } from '../i18n/en.ts';

export interface TourStep {
  id: keyof typeof en.tour.steps;
  target?: string;
}

/** In order: welcome, the top bar, the overview, where the tour comes back from, done. */
export const TOUR_STEPS: TourStep[] = [
  { id: 'welcome' },
  { id: 'org', target: 'org' },
  { id: 'navOverview', target: 'nav-overview' },
  { id: 'navAlarms', target: 'nav-alarms' },
  { id: 'navDevices', target: 'nav-devices' },
  { id: 'navLora', target: 'nav-lora' },
  { id: 'navHaccp', target: 'nav-haccp' },
  { id: 'navMaintenance', target: 'nav-maintenance' },
  { id: 'navEnergy', target: 'nav-energy' },
  { id: 'navSettings', target: 'nav-settings' },
  { id: 'navExports', target: 'nav-exports' },
  { id: 'live', target: 'live' },
  { id: 'alarmBanner', target: 'alarm-banner' },
  { id: 'views', target: 'views' },
  { id: 'asset', target: 'asset' },
  { id: 'menu', target: 'user-menu' },
  { id: 'done' },
];

/** The steps to show, given which targets are on screen. */
export const presentSteps = (steps: TourStep[], present: (target: string) => boolean): TourStep[] =>
  steps.filter((s) => !s.target || present(s.target));

/** The step after (or before) `index`, kept within the tour. */
export const moveStep = (index: number, by: 1 | -1, count: number): number => Math.min(Math.max(index + by, 0), count - 1);

/** Whether the tour opens by itself: once per user and browser, and never on a screen that only shows. */
export const shouldAutoStart = (o: { seen: boolean; readOnly: boolean; kiosk: boolean; impersonating: boolean }): boolean =>
  !o.seen && !o.readOnly && !o.kiosk && !o.impersonating;

const seenKey = (userId: string) => `cck.tourSeen.${userId}`;

export function tourSeen(userId: string): boolean {
  try {
    return localStorage.getItem(seenKey(userId)) === '1';
  } catch {
    return true; // storage unavailable: better no tour than the tour at every visit
  }
}

export function markTourSeen(userId: string): void {
  try {
    localStorage.setItem(seenKey(userId), '1');
  } catch {
    // storage unavailable
  }
}

interface Box { top: number; left: number; width: number; height: number }

/**
 * Where the card goes: below the target if it fits, else above, else over the bottom of the screen; centred on
 * the target horizontally, kept `margin` from the edges. Without a target, centred on the screen.
 */
export function placeCard(target: Box | null, card: { width: number; height: number },
  viewport: { width: number; height: number }, margin = 16, gap = 12): { top: number; left: number } {
  const clampX = (x: number) => Math.min(Math.max(x, margin), Math.max(margin, viewport.width - card.width - margin));
  if (!target) return { top: Math.max(margin, (viewport.height - card.height) / 2), left: clampX((viewport.width - card.width) / 2) };
  const left = clampX(target.left + target.width / 2 - card.width / 2);
  const below = target.top + target.height + gap;
  if (below + card.height <= viewport.height - margin) return { top: below, left };
  const above = target.top - gap - card.height;
  if (above >= margin) return { top: above, left };
  return { top: Math.max(margin, viewport.height - card.height - margin), left };
}
